import { useEffect, useState } from 'react'
import { authors, type AuthorSummary } from '../api/endpoints'
import { Select } from './Input'

export interface AuthorPickerProps {
  /** Linked User.id. Pass `null` to mean "no author" (the post keeps
   *  whatever authorName the caller sets, or 'Editorial Team'). */
  value: number | null
  onChange: (userId: number | null) => void
  disabled?: boolean
  className?: string
}

/** Dropdown listing every user who can author content (role='author'
 *  or has an authorSlug set). The server stores the linked User id
 *  on BlogPost.authorUserId; denormalized name/avatar are populated
 *  server-side, so the parent only needs to send authorUserId.
 *
 *  If the API call fails (e.g., on a fresh deploy with no authors),
 *  we render an empty dropdown with just the "—" placeholder so the
 *  editor still works. */
export function AuthorPicker({ value, onChange, disabled, className }: AuthorPickerProps) {
  const [list, setList] = useState<AuthorSummary[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    authors
      .list()
      .then((r) => {
        if (cancelled) return
        setList(r.authors)
      })
      .catch(() => {
        // Silently degrade — the post editor still works without
        // an author picker. Toast is noisy here.
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Select
      value={value == null ? '' : String(value)}
      onChange={(e) => {
        const v = e.target.value
        onChange(v === '' ? null : Number(v))
      }}
      disabled={disabled || loading}
      className={className}
    >
      <option value="">— No author —</option>
      {list.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
          {a.role === 'admin' ? ' (admin)' : ''}
          {a.postCount > 0 ? ` · ${a.postCount} post${a.postCount === 1 ? '' : 's'}` : ''}
        </option>
      ))}
    </Select>
  )
}
